import React, {
    useEffect,
    useState
} from "react";

import {
    useBrandProducts
} from "../context/BrandProductContext";

import ProductFormModal
    from "../components/products/ProductFormModal";

import ProductBasicInfo
    from "../components/products/ProductBasicInfo";

import ProductMedia
    from "../components/products/ProductMedia";

import ProductPricing
    from "../components/products/ProductPricing";

import ProductInventory
    from "../components/products/ProductInventory";

import ProductShipping
    from "../components/products/ProductShipping";

import ProductVariants
    from "../components/products/ProductVariants";

import ProductSEO
    from "../components/products/ProductSEO";

import ProductVisibility
    from "../components/products/ProductVisibility";

import ProductAttributes
    from "../components/products/ProductAttributes";

import {
    uploadProductMedia
} from "../services/ProductService";

import pb
    from "../../lib/pb";


export default function BrandProducts() {

    const {

        products,

        loading,

        error,

        addProduct,

        editProduct,

        removeProduct,

        variants,

        shipping,

        loadProductVariants,

        loadProductShipping,

        createVariant,

        updateVariant,

        deleteVariant,

        saveProductShipping

    } = useBrandProducts();


    /*
    ==================================================
    PRODUCT EDITOR
    ==================================================
    */

    const [editorOpen, setEditorOpen] =
        useState(false);

    const [editingProduct, setEditingProduct] =
        useState(null);

    const [saving, setSaving] =
        useState(false);

    const [deleting, setDeleting] =
        useState(null);

    const [activeSection, setActiveSection] =
        useState("basic");


    /*
    ==================================================
    PRODUCT STATE
    ==================================================
    */

    const [product, setProduct] = useState({

        title: "",

        slug: "",

        shortDescription: "",

        description: "",

        category: "",

        tags: "",

        sku: "",

        barcode: "",

        price: "",

        comparePrice: "",

        costPrice: "",

        currency: "INR",

        stock: 0,

        lowStock: 5,

        trackInventory: true,

        allowBackorder: false,

        weight: "",

        length_width_height: "",

        status: "draft",

        visibility: "public",

        featured: false,

        isDigital: false,

        verified: false,

        rating: 0,

        reviewCount: 0,

        sold: 0,

        views: 0,

        favorites: 0,

        seoTitle: "",

        seoDescription: "",

        images: null,

        media: [],

        coverIndex: 0

    });


    /*
    ==================================================
    EDITOR SECTIONS
    ==================================================
    */

    const sections = [

        {
            id: "basic",
            label: "Basic Information"
        },

        {
            id: "media",
            label: "Media"
        },

        {
            id: "pricing",
            label: "Pricing"
        },

        {
            id: "inventory",
            label: "Inventory"
        },

        {
            id: "shipping",
            label: "Shipping"
        },

        {
            id: "variants",
            label: "Variants"
        },

        {
            id: "seo",
            label: "SEO"
        },

        {
            id: "visibility",
            label: "Visibility"
        },

        {
            id: "attributes",
            label: "Attributes"
        }

    ];


    /*
    ==================================================
    OPEN CREATE
    ==================================================
    */

    const openCreate = () => {

        setEditingProduct(null);

        setProduct({

            title: "",

            slug: "",

            shortDescription: "",

            description: "",

            category: "",

            tags: "",

            sku: "",

            barcode: "",

            price: "",

            comparePrice: "",

            costPrice: "",

            currency: "INR",

            stock: 0,

            lowStock: 5,

            trackInventory: true,

            allowBackorder: false,

            weight: "",

            length_width_height: "",

            status: "draft",

            visibility: "public",

            featured: false,

            isDigital: false,

            verified: false,

            rating: 0,

            reviewCount: 0,

            sold: 0,

            views: 0,

            favorites: 0,

            seoTitle: "",

            seoDescription: "",

            images: null,

            media: [],

            coverIndex: 0

        });

        setActiveSection("basic");

        setEditorOpen(true);

    };


    /*
    ==================================================
    OPEN EDIT
    ==================================================
    */

    const openEdit = async (item) => {

        setEditingProduct(item);

        /*
        Existing PocketBase media is stored
        in the `images` field.

        New File objects remain inside
        `product.media`.
        */

        setProduct({

            ...item,

            media: [],

            coverIndex: 0,

            price:
                item.price ?? "",

            comparePrice:
                item.comparePrice ?? "",

            costPrice:
                item.costPrice ?? "",

            stock:
                item.stock ?? 0,

            lowStock:
                item.lowStock ?? 5,

            trackInventory:
                item.trackInventory !== false,

            allowBackorder:
                item.allowBackorder === true,

            featured:
                item.featured === true,

            isDigital:
                item.isDigital === true,

            verified:
                item.verified === true

        });

        setActiveSection("basic");

        setEditorOpen(true);


        /*
        ----------------------------------------------
        Load separate collections
        ----------------------------------------------
        */

        if (item.id) {

            try {

                await loadProductVariants(
                    item.id
                );

            }

            catch (err) {

                console.error(
                    "Failed to load variants:",
                    err
                );

            }


            try {

                await loadProductShipping(
                    item.id
                );

            }

            catch (err) {

                console.error(
                    "Failed to load shipping:",
                    err
                );

            }

        }

    };


    /*
    ==================================================
    CLOSE EDITOR
    ==================================================
    */

    const closeEditor = () => {

        if (saving) {

            return;

        }

        setEditorOpen(false);

        setEditingProduct(null);

    };


    /*
    ==================================================
    SAVE PRODUCT
    ==================================================
    */

    const handleSave = async () => {

        /*
        ----------------------------------------------
        Validate product title
        ----------------------------------------------
        */

        if (!product.title?.trim()) {

            alert(
                "Please enter a product name."
            );

            setActiveSection("basic");

            return;

        }


        try {

            setSaving(true);


            /*
            ------------------------------------------
            Collect newly selected media files
            ------------------------------------------

            ProductMedia stores File objects
            inside product.media.

            PocketBase `images` is currently a
            SINGLE file field.

            Therefore ONLY ONE file can be uploaded.

            We use the selected coverIndex.

            ------------------------------------------
            */

            const mediaFiles = Array.from(
                product.media || []
            ).filter(file => {

                return (

                    typeof File !== "undefined" &&

                    file instanceof File

                );

            });


            let coverFile = null;


            if (mediaFiles.length > 0) {

                /*
                ProductMedia's coverIndex refers
                to the position inside product.media.

                Find the selected cover.

                If the selected index does not
                correspond to a File, fall back
                to the first uploaded File.
                */

                const selectedMedia =
                    product.media?.[
                        product.coverIndex
                    ];


                if (

                    typeof File !== "undefined" &&

                    selectedMedia instanceof File

                ) {

                    coverFile =
                        selectedMedia;

                }

                else {

                    coverFile =
                        mediaFiles[0];

                }

            }


            /*
            ------------------------------------------
            Only send fields belonging to
            PocketBase products collection.
            ------------------------------------------
            */

            const productData = {

                title:
                    product.title,

                slug:
                    product.slug,

                shortDescription:
                    product.shortDescription,

                description:
                    product.description,

                category:
                    product.category,

                tags:
                    product.tags,

                sku:
                    product.sku,

                barcode:
                    product.barcode,

                price:
                    product.price === ""
                        ? 0
                        : Number(product.price),

                comparePrice:
                    product.comparePrice === ""
                        ? 0
                        : Number(product.comparePrice),

                costPrice:
                    product.costPrice === ""
                        ? 0
                        : Number(product.costPrice),

                currency:
                    product.currency || "INR",

                stock:
                    product.stock === ""
                        ? 0
                        : Number(product.stock),

                lowStock:
                    product.lowStock === ""
                        ? 0
                        : Number(product.lowStock),

                trackInventory:
                    product.trackInventory !== false,

                allowBackorder:
                    product.allowBackorder === true,

                weight:
                    product.weight,

                length_width_height:
                    product.length_width_height,

                status:
                    product.status || "draft",

                visibility:
                    product.visibility || "public",

                featured:
                    product.featured === true,

                isDigital:
                    product.isDigital === true,

                verified:
                    product.verified === true,

                seoTitle:
                    product.seoTitle,

                seoDescription:
                    product.seoDescription

            };


            /*
            ==================================================
            CREATE
            ==================================================
            */

            if (!editingProduct) {

                /*
                ------------------------------------------
                First create the actual PocketBase product.
                ------------------------------------------
                */

                const created =
                    await addProduct(
                        productData
                    );


                /*
                ------------------------------------------
                Now that the product exists, upload
                the selected COVER file.

                IMPORTANT:
                `images` is currently a SINGLE file
                field in PocketBase.
                ------------------------------------------
                */

                if (

                    created?.id &&

                    coverFile

                ) {

                    await uploadProductMedia(

                        created.id,

                        [coverFile]

                    );

                }

            }


            /*
            ==================================================
            UPDATE
            ==================================================
            */

            else {

                /*
                ------------------------------------------
                Update normal product fields first.
                ------------------------------------------
                */

                const updated =
                    await editProduct(

                        editingProduct.id,

                        productData

                    );


                /*
                ------------------------------------------
                Upload newly selected cover separately.

                Existing PocketBase image remains unless
                a new cover is selected.
                ------------------------------------------
                */

                if (

                    updated?.id &&

                    coverFile

                ) {

                    await uploadProductMedia(

                        updated.id,

                        [coverFile]

                    );

                }

            }


            /*
            ------------------------------------------
            Close after successful save
            ------------------------------------------
            */

            setEditorOpen(false);

            setEditingProduct(null);

            setProduct({

                title: "",

                slug: "",

                shortDescription: "",

                description: "",

                category: "",

                tags: "",

                sku: "",

                barcode: "",

                price: "",

                comparePrice: "",

                costPrice: "",

                currency: "INR",

                stock: 0,

                lowStock: 5,

                trackInventory: true,

                allowBackorder: false,

                weight: "",

                length_width_height: "",

                status: "draft",

                visibility: "public",

                featured: false,

                isDigital: false,

                verified: false,

                rating: 0,

                reviewCount: 0,

                sold: 0,

                views: 0,

                favorites: 0,

                seoTitle: "",

                seoDescription: "",

                images: null,

                media: [],

                coverIndex: 0

            });

        }

        catch (err) {

            console.error(
                "Product save failed:",
                err
            );

            alert(
                err?.message ||
                "Unable to save the product."
            );

        }

        finally {

            setSaving(false);

        }

    };


    /*
    ==================================================
    DELETE PRODUCT
    ==================================================
    */

    const handleDelete = async (item) => {

        const confirmed =
            window.confirm(

                `Are you sure you want to delete "${item.title}"?`

            );


        if (!confirmed) {

            return;

        }


        try {

            setDeleting(item.id);

            await removeProduct(
                item.id
            );

        }

        catch (err) {

            console.error(
                "Product deletion failed:",
                err
            );

            alert(
                err?.message ||
                "Unable to delete the product."
            );

        }

        finally {

            setDeleting(null);

        }

    };


    /*
    ==================================================
    PRODUCT STATUS
    ==================================================
    */

    const getStatusStyle = (status) => {

        if (status === "published") {

            return {

                background: "#dcfce7",

                color: "#166534"

            };

        }


        if (status === "hidden") {

            return {

                background: "#fef3c7",

                color: "#92400e"

            };

        }


        if (status === "archived") {

            return {

                background: "#e5e7eb",

                color: "#374151"

            };

        }


        return {

            background: "#f3e8ff",

            color: "#7e22ce"

        };

    };


    /*
    ==================================================
    EDITOR CONTENT
    ==================================================
    */

    const renderEditorSection = () => {

        switch (activeSection) {

            case "basic":

                return (

                    <ProductBasicInfo

                        product={product}

                        setProduct={setProduct}

                    />

                );


            case "media":

                return (

                    <ProductMedia

                        product={product}

                        setProduct={setProduct}

                    />

                );


            case "pricing":

                return (

                    <ProductPricing

                        product={product}

                        setProduct={setProduct}

                    />

                );


            case "inventory":

                return (

                    <ProductInventory

                        product={product}

                        setProduct={setProduct}

                    />

                );


            case "shipping":

                return (

                    <ProductShipping

                        product={product}

                        setProduct={setProduct}

                        shipping={shipping}

                        onSave={async (data) => {

                            if (!editingProduct?.id) {

                                alert(
                                    "Save the product first before configuring shipping."
                                );

                                return;

                            }

                            await saveProductShipping(

                                editingProduct.id,

                                data

                            );

                        }}

                    />

                );


            case "variants":

                return (

                    <ProductVariants

                        product={product}

                        setProduct={setProduct}

                        variants={variants}

                        createVariant={createVariant}

                        updateVariant={updateVariant}

                        deleteVariant={deleteVariant}

                    />

                );


            case "seo":

                return (

                    <ProductSEO

                        product={product}

                        setProduct={setProduct}

                    />

                );


            case "visibility":

                return (

                    <ProductVisibility

                        product={product}

                        setProduct={setProduct}

                    />

                );


            case "attributes":

                return (

                    <ProductAttributes

                        product={product}

                        setProduct={setProduct}

                    />

                );


            default:

                return null;

        }

    };


    /*
    ==================================================
    LOADING
    ==================================================
    */

    if (loading) {

        return (

            <div
                style={{
                    padding: 40
                }}
            >

                <h1>
                    Products
                </h1>

                <p
                    style={{
                        color: "#666"
                    }}
                >
                    Loading products...
                </p>

            </div>

        );

    }


    /*
    ==================================================
    ERROR
    ==================================================
    */

    if (error) {

        return (

            <div
                style={{
                    padding: 40
                }}
            >

                <h1>
                    Products
                </h1>

                <div
                    style={{
                        marginTop: 20,

                        padding: 20,

                        borderRadius: 12,

                        background: "#fee2e2",

                        color: "#991b1b"
                    }}
                >

                    {error?.message ||
                        "Unable to load products."}

                </div>


                <button

                    onClick={() =>
                        window.location.reload()
                    }

                    style={{
                        marginTop: 20,

                        padding: "10px 18px",

                        border: "none",

                        borderRadius: 8,

                        background:
                            "rgb(255,0,204)",

                        color: "#fff",

                        cursor: "pointer",

                        fontWeight: 600
                    }}

                >
                    Retry

                </button>

            </div>

        );

    }


    /*
    ==================================================
    PAGE
    ==================================================
    */

    return (

        <div
            style={{
                minHeight: "100%",
                paddingBottom: 40
            }}
        >

            {/* =================================================
            PAGE HEADER
            ================================================= */}

            <div
                style={{
                    display: "flex",

                    justifyContent:
                        "space-between",

                    alignItems:
                        "center",

                    marginBottom: 25
                }}
            >

                <div>

                    <h1
                        style={{
                            margin: 0,

                            fontSize: 30,

                            fontWeight: 750
                        }}
                    >
                        Products
                    </h1>

                    <p
                        style={{
                            marginTop: 8,

                            marginBottom: 0,

                            color: "#6b7280"
                        }}
                    >
                        Manage your brand's products,
                        inventory, pricing and marketplace
                        availability.
                    </p>

                </div>


                <button

                    onClick={openCreate}

                    style={{
                        padding:
                            "13px 22px",

                        border: "none",

                        borderRadius: 11,

                        background:
                            "rgb(255,0,204)",

                        color: "#fff",

                        cursor: "pointer",

                        fontWeight: 700,

                        fontSize: 14,

                        boxShadow:
                            "0 8px 20px rgba(255,0,204,.22)"
                    }}

                >
                    + Add Product
                </button>

            </div>


            {/* =================================================
            SUMMARY
            ================================================= */}

            <div
                style={{
                    display: "grid",

                    gridTemplateColumns:
                        "repeat(auto-fit,minmax(180px,1fr))",

                    gap: 18,

                    marginBottom: 25
                }}
            >

                <SummaryCard

                    label="Total Products"

                    value={
                        products.length
                    }

                />

                <SummaryCard

                    label="Published"

                    value={

                        products.filter(

                            x =>
                                x.status ===
                                "published"

                        ).length

                    }

                />

                <SummaryCard

                    label="Drafts"

                    value={

                        products.filter(

                            x =>
                                x.status ===
                                "draft"

                        ).length

                    }

                />

                <SummaryCard

                    label="Low Stock"

                    value={

                        products.filter(

                            x =>

                                x.trackInventory &&

                                Number(
                                    x.stock || 0
                                ) <=

                                Number(
                                    x.lowStock || 0
                                )

                        ).length

                    }

                />

            </div>


            {/* =================================================
            PRODUCT TABLE
            ================================================= */}

            <div
                style={{
                    background: "#fff",

                    borderRadius: 16,

                    overflow: "hidden",

                    boxShadow:
                        "0 8px 25px rgba(0,0,0,.05)"
                }}
            >

                <div
                    style={{
                        padding:
                            "20px 24px",

                        borderBottom:
                            "1px solid #eee",

                        fontWeight: 700,

                        fontSize: 17
                    }}
                >
                    All Products
                </div>


                {products.length === 0 ? (

                    <div
                        style={{
                            padding: 60,

                            textAlign: "center"
                        }}
                    >

                        <div
                            style={{
                                fontSize: 42,

                                marginBottom: 15
                            }}
                        >
                            🛍️
                        </div>

                        <h3
                            style={{
                                margin: 0
                            }}
                        >
                            No products yet
                        </h3>

                        <p
                            style={{
                                color: "#777",

                                marginTop: 8
                            }}
                        >
                            Create your first product
                            to make it available in
                            your brand marketplace.
                        </p>

                        <button

                            onClick={openCreate}

                            style={{
                                marginTop: 15,

                                padding:
                                    "11px 20px",

                                border: "none",

                                borderRadius: 9,

                                background:
                                    "rgb(255,0,204)",

                                color: "#fff",

                                cursor: "pointer",

                                fontWeight: 700
                            }}

                        >
                            Add First Product
                        </button>

                    </div>

                ) : (

                    <div
                        style={{
                            overflowX: "auto"
                        }}
                    >

                        <table
                            style={{
                                width: "100%",

                                borderCollapse:
                                    "collapse"
                            }}
                        >

                            <thead>

                                <tr
                                    style={{
                                        background:
                                            "#fafafa"
                                    }}
                                >

                                    <th
                                        style={
                                            thStyle
                                        }
                                    >
                                        Product
                                    </th>

                                    <th
                                        style={
                                            thStyle
                                        }
                                    >
                                        SKU
                                    </th>

                                    <th
                                        style={
                                            thStyle
                                        }
                                    >
                                        Price
                                    </th>

                                    <th
                                        style={
                                            thStyle
                                        }
                                    >
                                        Stock
                                    </th>

                                    <th
                                        style={
                                            thStyle
                                        }
                                    >
                                        Status
                                    </th>

                                    <th
                                        style={
                                            thStyle
                                        }
                                    >
                                        Actions
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {products.map(

                                    item => (

                                        <tr

                                            key={
                                                item.id
                                            }

                                            style={{
                                                borderTop:
                                                    "1px solid #eee"
                                            }}
                                        >

                                            <td
                                                style={{
                                                    padding:
                                                        "16px 20px"
                                                }}
                                            >

                                                <div
                                                    style={{
                                                        display:
                                                            "flex",

                                                        alignItems:
                                                            "center",

                                                        gap: 12
                                                    }}
                                                >

                                                    <ProductThumbnail
                                                        product={
                                                            item
                                                        }
                                                    />

                                                    <div>

                                                        <div
                                                            style={{
                                                                fontWeight:
                                                                    700
                                                            }}
                                                        >
                                                            {
                                                                item.title ||
                                                                "Untitled Product"
                                                            }
                                                        </div>

                                                        <div
                                                            style={{
                                                                fontSize: 12,

                                                                color:
                                                                    "#888",

                                                                marginTop: 4
                                                            }}
                                                        >
                                                            {
                                                                item.category ||
                                                                "Uncategorized"
                                                            }
                                                        </div>

                                                    </div>

                                                </div>

                                            </td>


                                            <td
                                                style={
                                                    tdStyle
                                                }
                                            >
                                                {
                                                    item.sku ||
                                                    "—"
                                                }
                                            </td>


                                            <td
                                                style={
                                                    tdStyle
                                                }
                                            >

                                                {
                                                    item.currency ||
                                                    "INR"
                                                }{" "}

                                                {
                                                    Number(
                                                        item.price ||
                                                        0
                                                    ).toLocaleString(
                                                        "en-IN"
                                                    )
                                                }

                                            </td>


                                            <td
                                                style={
                                                    tdStyle
                                                }
                                            >

                                                {
                                                    item.trackInventory

                                                        ? item.stock ??
                                                        0

                                                        : "Not tracked"
                                                }

                                            </td>


                                            <td
                                                style={
                                                    tdStyle
                                                }
                                            >

                                                <span
                                                    style={{
                                                        ...getStatusStyle(
                                                            item.status
                                                        ),

                                                        display:
                                                            "inline-block",

                                                        padding:
                                                            "5px 10px",

                                                        borderRadius:
                                                            999,

                                                        fontSize:
                                                            12,

                                                        fontWeight:
                                                            700,

                                                        textTransform:
                                                            "capitalize"
                                                    }}
                                                >
                                                    {
                                                        item.status ||
                                                        "draft"
                                                    }
                                                </span>

                                            </td>


                                            <td
                                                style={
                                                    tdStyle
                                                }
                                            >

                                                <div
                                                    style={{
                                                        display:
                                                            "flex",

                                                        gap: 8
                                                    }}
                                                >

                                                    <button

                                                        onClick={() =>
                                                            openEdit(
                                                                item
                                                            )
                                                        }

                                                        style={
                                                            actionButtonStyle
                                                        }

                                                    >
                                                        Edit
                                                    </button>


                                                    <button

                                                        onClick={() =>
                                                            handleDelete(
                                                                item
                                                            )
                                                        }

                                                        disabled={
                                                            deleting ===
                                                            item.id
                                                        }

                                                        style={{
                                                            ...actionButtonStyle,

                                                            color:
                                                                "#dc2626",

                                                            borderColor:
                                                                "#fecaca"
                                                        }}

                                                    >

                                                        {
                                                            deleting ===
                                                            item.id

                                                                ? "Deleting..."

                                                                : "Delete"
                                                        }

                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    )

                                )}

                            </tbody>

                        </table>

                    </div>

                )}

            </div>


            {/* =================================================
            PRODUCT EDITOR
            ================================================= */}

            <ProductFormModal

                open={
                    editorOpen
                }

                onClose={
                    closeEditor
                }

                title={

                    editingProduct

                        ? "Edit Product"

                        : "Add Product"

                }

                onSave={
                    handleSave
                }

                saving={
                    saving
                }

            >

                <div
                    style={{
                        display:
                            "flex",

                        flexDirection:
                            "column",

                        gap: 25
                    }}
                >

                    {/* -----------------------------------------
                    Editor Navigation
                    ----------------------------------------- */}

                    <div
                        style={{
                            display:
                                "flex",

                            gap: 8,

                            overflowX:
                                "auto",

                            paddingBottom:
                                5
                        }}
                    >

                        {sections.map(

                            section => (

                                <button

                                    key={
                                        section.id
                                    }

                                    type="button"

                                    onClick={() =>
                                        setActiveSection(
                                            section.id
                                        )
                                    }

                                    style={{
                                        whiteSpace:
                                            "nowrap",

                                        padding:
                                            "10px 14px",

                                        border:
                                            "1px solid #ddd",

                                        borderRadius:
                                            9,

                                        cursor:
                                            "pointer",

                                        background:

                                            activeSection ===
                                            section.id

                                                ? "rgb(255,0,204)"

                                                : "#fff",

                                        color:

                                            activeSection ===
                                            section.id

                                                ? "#fff"

                                                : "#444",

                                        fontWeight:
                                            600
                                    }}

                                >
                                    {
                                        section.label
                                    }
                                </button>

                            )

                        )}

                    </div>


                    {/* -----------------------------------------
                    Editor Section
                    ----------------------------------------- */}

                    <div
                        style={{
                            background:
                                "#fff",

                            borderRadius:
                                16,

                            padding:
                                24,

                            border:
                                "1px solid #eee"
                        }}
                    >

                        {
                            renderEditorSection()
                        }

                    </div>

                </div>

            </ProductFormModal>

        </div>

    );

}


/*
==================================================
SUMMARY CARD
==================================================
*/

function SummaryCard({

    label,

    value

}) {

    return (

        <div
            style={{
                background:
                    "#fff",

                borderRadius:
                    14,

                padding:
                    22,

                boxShadow:
                    "0 8px 25px rgba(0,0,0,.04)"
            }}
        >

            <div
                style={{
                    color:
                        "#777",

                    fontSize:
                        13,

                    fontWeight:
                        600
                }}
            >
                {label}
            </div>


            <div
                style={{
                    marginTop:
                        8,

                    fontSize:
                        28,

                    fontWeight:
                        750
                }}
            >
                {value}
            </div>

        </div>

    );

}


/*
==================================================
PRODUCT THUMBNAIL
==================================================
*/

function ProductThumbnail({

    product

}) {

    /*
    PocketBase's `images` field contains
    filenames, not complete browser URLs.

    Generate the actual PocketBase
    file URL using pb.files.getURL().
    */

    let image = null;


    /*
    Multiple-file fallback
    */

    if (

        Array.isArray(
            product.images
        ) &&

        product.images.length > 0

    ) {

        const filename =
            product.images[0];

        if (filename) {

            image =
                pb.files.getURL(

                    product,

                    filename

                );

        }

    }


    /*
    Single-file field
    */

    else if (

        typeof product.images ===
        "string" &&

        product.images

    ) {

        image =
            pb.files.getURL(

                product,

                product.images

            );

    }


    return (

        <div
            style={{
                width: 52,

                height: 52,

                borderRadius: 10,

                overflow: "hidden",

                background:
                    "#f3f4f6",

                display:
                    "flex",

                alignItems:
                    "center",

                justifyContent:
                    "center",

                flexShrink: 0
            }}
        >

            {image ? (

                <img

                    src={
                        image
                    }

                    alt=""

                    style={{
                        width:
                            "100%",

                        height:
                            "100%",

                        objectFit:
                            "cover"
                    }}

                />

            ) : (

                <span
                    style={{
                        fontSize:
                            22
                    }}
                >
                    🛍️
                </span>

            )}

        </div>

    );

}


/*
==================================================
TABLE STYLES
==================================================
*/

const thStyle = {

    padding:
        "14px 20px",

    textAlign:
        "left",

    fontSize:
        12,

    color:
        "#6b7280",

    fontWeight:
        700,

    textTransform:
        "uppercase",

    letterSpacing:
        ".04em"

};


const tdStyle = {

    padding:
        "16px 20px",

    fontSize:
        14,

    color:
        "#374151"

};


const actionButtonStyle = {

    padding:
        "7px 12px",

    border:
        "1px solid #ddd",

    borderRadius:
        8,

    background:
        "#fff",

    cursor:
        "pointer",

    fontSize:
        12,

    fontWeight:
        700

};